import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const MV_STRATEGIES = new Set(['market_value', 'sb_baseline', 'market_and_implied_ev']);

// SectionShell mirrors HotPropsFiltersModal's container — a quiet bordered card with
// a left emerald rail when the section is "active" (i.e. its filter is enabled).
// We add a switch in the header so the user can quickly enable/disable the filter,
// preserving the existing per-filter `enabled` flag without changing the data model.
function SectionShell({ label, hint, enabled, onToggle, children }) {
    return (
        <section
            className={`relative rounded-lg border bg-slate-900/40 px-4 pt-3.5 pb-4 transition-colors ${
                enabled ? 'border-emerald-500/40' : 'border-slate-800/80 hover:border-slate-700/80'
            }`}
        >
            {enabled && (
                <span
                    aria-hidden="true"
                    className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r bg-emerald-400"
                />
            )}
            <header className="mb-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h4 className="text-[10px] font-mono font-semibold text-slate-400 uppercase tracking-[0.14em]">
                        {label}
                    </h4>
                    {hint && (
                        <p className="mt-1 text-[11px] text-slate-500 leading-snug">{hint}</p>
                    )}
                </div>
                {typeof onToggle === 'function' && (
                    <button
                        type="button"
                        onClick={onToggle}
                        role="switch"
                        aria-checked={enabled}
                        aria-label={`Toggle ${label}`}
                        className={`relative inline-flex shrink-0 items-center rounded-full transition-colors mt-0.5 p-0 ${
                            enabled ? 'bg-emerald-500' : 'bg-slate-700'
                        }`}
                        style={{ width: '34px', height: '18px' }}
                    >
                        <span
                            aria-hidden="true"
                            className="block rounded-full bg-white shadow transition-transform duration-150"
                            style={{
                                width: '14px',
                                height: '14px',
                                transform: enabled ? 'translateX(18px)' : 'translateX(2px)',
                            }}
                        />
                    </button>
                )}
            </header>
            {enabled && children && (
                <div className="pt-1">{children}</div>
            )}
        </section>
    );
}

// Generic threshold slider — shares HotPropsFiltersModal's typography/colors
// but accepts a custom `formatValue` so we can render % vs hours vs raw integers.
function ThresholdSlider({
    value,
    min,
    max,
    step = 1,
    onChange,
    formatValue,
    formatBound,
    valueLabel = 'Value',
}) {
    const display = formatValue ? formatValue(value) : Number(value);
    const minLabel = formatBound ? formatBound(min) : String(min);
    const maxLabel = formatBound ? formatBound(max) : String(max);

    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                    {valueLabel}
                </span>
                <span className="text-xl font-mono font-bold text-emerald-400 tabular-nums leading-none">
                    {display}
                </span>
            </div>

            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer accent-emerald-500
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg
                    [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600"
            />

            <div className="flex justify-between mt-1.5 text-[9px] font-mono text-slate-700 uppercase tracking-wider">
                <span>{minLabel}</span>
                <span>{maxLabel}</span>
            </div>
        </div>
    );
}

function BookmakerChip({ bookmaker, label, logo, selected, onClick }) {
    return (
        <button
            type="button"
            onClick={() => onClick(bookmaker)}
            title={selected ? `Excluding ${label}` : label}
            className={`group inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-all duration-150 ${
                selected
                    ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
                    : 'border-slate-800 bg-slate-900/40 text-slate-400 hover:border-slate-700 hover:text-slate-200'
            }`}
        >
            {logo ? (
                <img
                    src={logo}
                    alt={label}
                    className={`w-3.5 h-3.5 rounded object-cover ${selected ? '' : 'opacity-60 group-hover:opacity-90'}`}
                />
            ) : (
                <span className="text-[11px]">{label}</span>
            )}
            <span>{label}</span>
            {selected && (
                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                </svg>
            )}
        </button>
    );
}

function FiltersModal({ filters, onApply, onClose, maxBookmakers, bookmakers, bookmakerLogos, bookmakerNames, strategy }) {
    const isMV = MV_STRATEGIES.has(strategy);
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    // ---- Field handlers (logic preserved from original implementation) ----
    const handleMinBookmakersToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minBookmakers: { ...prev.minBookmakers, enabled: !prev.minBookmakers.enabled }
        }));
    };
    const handleMinBookmakersChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minBookmakers: { ...prev.minBookmakers, value: Number(value) }
        }));
    };

    const handleExcludeBestToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            excludeBestBookmakers: { ...prev.excludeBestBookmakers, enabled: !prev.excludeBestBookmakers.enabled }
        }));
    };

    const handleMinEvToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minEv: { ...prev.minEv, enabled: !prev.minEv.enabled }
        }));
    };
    const handleMinEvChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minEv: { ...prev.minEv, value: Number(value) }
        }));
    };

    const handleMinArbToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minArb: { ...prev.minArb, enabled: !prev.minArb.enabled }
        }));
    };
    const handleMinArbChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minArb: { ...prev.minArb, value: Number(value) }
        }));
    };

    const handleArbExcludeToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            excludeBookmakers: { ...prev.excludeBookmakers, enabled: !prev.excludeBookmakers.enabled }
        }));
    };
    const handleArbExcludeBookmakerClick = (bookmaker) => {
        setLocalFilters(prev => {
            const current = prev.excludeBookmakers.value;
            const updated = current.includes(bookmaker)
                ? current.filter(b => b !== bookmaker)
                : [...current, bookmaker];
            return {
                ...prev,
                excludeBookmakers: { ...prev.excludeBookmakers, value: updated }
            };
        });
    };

    const handleExcludeBookmakerClick = (bookmaker) => {
        setLocalFilters(prev => {
            const current = prev.excludeBestBookmakers.value;
            const updated = current.includes(bookmaker)
                ? current.filter(b => b !== bookmaker)
                : [...current, bookmaker];
            return {
                ...prev,
                excludeBestBookmakers: { ...prev.excludeBestBookmakers, value: updated }
            };
        });
    };

    const handleMinImpliedEvToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minImpliedEv: { ...prev.minImpliedEv, enabled: !prev.minImpliedEv?.enabled }
        }));
    };
    const handleMinImpliedEvChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minImpliedEv: { ...prev.minImpliedEv, value: Number(value) }
        }));
    };

    const handleMinSampleSizeToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minSampleSize: { ...prev.minSampleSize, enabled: !prev.minSampleSize?.enabled }
        }));
    };
    const handleMinSampleSizeChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minSampleSize: { ...prev.minSampleSize, value: Number(value) }
        }));
    };

    const handleMinOddsToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minOdds: { ...prev.minOdds, enabled: !prev.minOdds?.enabled }
        }));
    };
    const handleMinOddsChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minOdds: { ...prev.minOdds, value }
        }));
    };
    const handleMinOddsBlur = () => {
        setLocalFilters(prev => {
            const num = parseFloat(prev.minOdds?.value);
            return {
                ...prev,
                minOdds: { ...prev.minOdds, value: isNaN(num) || num < 1.01 ? 1.01 : num }
            };
        });
    };

    const handleStartWithinToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            startWithin: { ...prev.startWithin, enabled: !prev.startWithin.enabled }
        }));
    };
    const handleStartWithinChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            startWithin: { ...prev.startWithin, value: Number(value) }
        }));
    };

    const handleApply = () => {
        onApply(localFilters);
        onClose();
    };

    // Strategy-specific defaults (logic preserved from original implementation).
    const handleReset = () => {
        let defaults;
        if (strategy === 'arbs') {
            defaults = {
                minArb: { enabled: true, value: 2 },
                excludeBookmakers: { enabled: false, value: [] },
            };
        } else if (strategy === 'sb_baseline') {
            defaults = {
                minBookmakers: { enabled: false, value: 2 },
                minEv: { enabled: true, value: 10 },
                startWithin: { enabled: false, value: 3 },
                excludeBestBookmakers: { enabled: false, value: [] },
            };
        } else if (strategy === 'market_and_implied_ev') {
            defaults = {
                minBookmakers: { enabled: true, value: 4 },
                minEv: { enabled: true, value: 10 },
                minImpliedEv: { enabled: false, value: 0 },
                minOdds: { enabled: false, value: 1.50 },
                minSampleSize: { enabled: false, value: 5 },
                startWithin: { enabled: false, value: 3 },
                excludeBestBookmakers: { enabled: false, value: [] },
            };
        } else if (isMV) {
            defaults = {
                minBookmakers: { enabled: true, value: 4 },
                minEv: { enabled: true, value: 10.5 },
                startWithin: { enabled: false, value: 3 },
                excludeBestBookmakers: { enabled: false, value: [] },
            };
        } else {
            defaults = {
                minBookmakers: { enabled: true, value: 5 },
                minEv: { enabled: true, value: 3 },
                startWithin: { enabled: false, value: 3 },
                excludeBestBookmakers: { enabled: false, value: [] },
            };
        }
        setLocalFilters(defaults);
    };

    const activeCount = useMemo(() => {
        return Object.entries(localFilters).filter(([, f]) => {
            if (!f?.enabled) return false;
            if (Array.isArray(f.value)) return f.value.length > 0;
            return true;
        }).length;
    }, [localFilters]);

    // Strategy-aware bounds for the Min EV/Edge slider — preserved from original.
    const evMin = (strategy === 'sb_baseline' || strategy === 'market_and_implied_ev') ? 10 : isMV ? 10.5 : 3;
    const evMax = isMV ? 30 : 20;
    const evStep = (strategy === 'sb_baseline' || strategy === 'market_and_implied_ev') ? 1 : isMV ? 0.5 : 1;

    return createPortal(
        <div
            className="fixed inset-0 flex items-end md:items-center justify-center p-0 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-fade-in"
            style={{ zIndex: 10000 }}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Snipe Feed Filters"
        >
            <div
                className="relative w-full md:max-w-xl bg-slate-900 border-t md:border border-slate-800 rounded-t-2xl md:rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col max-h-[85vh] md:max-h-[90vh] animate-slide-up md:animate-fade-in"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)' }}
            >
                {/* Mobile grab handle */}
                <div aria-hidden="true" className="md:hidden flex justify-center pt-2">
                    <span className="block w-10 h-1 rounded-full bg-slate-700/80" />
                </div>

                {/* Top emerald hairline + subtle glow (desktop only) */}
                <div className="hidden md:block absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-20 bg-emerald-500/10 blur-3xl pointer-events-none" />

                {/* Header */}
                <div className="relative flex items-center justify-between px-5 pt-3 md:pt-5 pb-4 border-b border-slate-800/80">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-800/60 border border-slate-700/50 flex items-center justify-center text-emerald-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-white tracking-tight">Filters</h3>
                            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.14em]">
                                {activeCount > 0 ? `${activeCount} active` : 'Defaults'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close filters"
                        className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-800/70 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto styled-scrollbar px-5 py-5 space-y-4">
                    {/* Min Arb% (Arbs only) */}
                    {strategy === 'arbs' && (
                        <SectionShell
                            label="Minimum Arb%"
                            hint="Only show arbitrage opportunities with at least this percentage."
                            enabled={!!localFilters.minArb?.enabled}
                            onToggle={handleMinArbToggle}
                        >
                            <ThresholdSlider
                                valueLabel="Min Arb"
                                value={localFilters.minArb?.value ?? 2}
                                min={1}
                                max={10}
                                step={0.5}
                                onChange={handleMinArbChange}
                                formatValue={(v) => `${Number(v).toFixed(1)}%`}
                                formatBound={(v) => `${v}%`}
                            />
                        </SectionShell>
                    )}

                    {/* Min Bookmakers (EV / MV — not arbs, not sb_baseline by default but the original
                        rendered for sb_baseline as well? The original code excluded sb_baseline, so we keep that behavior). */}
                    {strategy !== 'arbs' && strategy !== 'sb_baseline' && (
                        <SectionShell
                            label="Minimum Bookmakers"
                            hint="Only show bets backed by at least this many bookmakers in the sample."
                            enabled={!!localFilters.minBookmakers?.enabled}
                            onToggle={handleMinBookmakersToggle}
                        >
                            <ThresholdSlider
                                valueLabel="Min Books"
                                value={localFilters.minBookmakers?.value ?? 2}
                                min={2}
                                max={maxBookmakers}
                                step={1}
                                onChange={handleMinBookmakersChange}
                                formatValue={(v) => Math.round(Number(v))}
                                formatBound={(v) => Math.round(Number(v))}
                            />
                        </SectionShell>
                    )}

                    {/* Min EV / Edge (EV / MV) */}
                    {strategy !== 'arbs' && (
                        <SectionShell
                            label={`Minimum ${isMV ? 'Edge' : 'EV'}%`}
                            hint={`Only show bets with at least this ${isMV ? 'edge' : 'expected value'}.`}
                            enabled={!!localFilters.minEv?.enabled}
                            onToggle={handleMinEvToggle}
                        >
                            <ThresholdSlider
                                valueLabel={`Min ${isMV ? 'Edge' : 'EV'}`}
                                value={localFilters.minEv?.value ?? evMin}
                                min={evMin}
                                max={evMax}
                                step={evStep}
                                onChange={handleMinEvChange}
                                formatValue={(v) => `+${Number(v).toFixed(evStep < 1 ? 1 : 0)}%`}
                                formatBound={(v) => `${v}%`}
                            />
                        </SectionShell>
                    )}

                    {/* Min Implied EV (AFL Market & Implied EV only) */}
                    {strategy === 'market_and_implied_ev' && (
                        <SectionShell
                            label="Minimum Implied EV%"
                            hint="Only show bets with at least this implied expected value."
                            enabled={!!localFilters.minImpliedEv?.enabled}
                            onToggle={handleMinImpliedEvToggle}
                        >
                            <ThresholdSlider
                                valueLabel="Min Implied EV"
                                value={localFilters.minImpliedEv?.value ?? 0}
                                min={-20}
                                max={50}
                                step={1}
                                onChange={handleMinImpliedEvChange}
                                formatValue={(v) => `${Number(v) >= 0 ? '+' : ''}${Math.round(Number(v))}%`}
                                formatBound={(v) => `${v >= 0 ? '+' : ''}${v}%`}
                            />
                        </SectionShell>
                    )}

                    {/* Min Sample Size (AFL only) */}
                    {strategy === 'market_and_implied_ev' && (
                        <SectionShell
                            label="Minimum Game Sample"
                            hint="Only show bets with at least this many games of hit-rate data."
                            enabled={!!localFilters.minSampleSize?.enabled}
                            onToggle={handleMinSampleSizeToggle}
                        >
                            <ThresholdSlider
                                valueLabel="Min Games"
                                value={localFilters.minSampleSize?.value ?? 1}
                                min={1}
                                max={30}
                                step={1}
                                onChange={handleMinSampleSizeChange}
                                formatValue={(v) => Math.round(Number(v))}
                                formatBound={(v) => Math.round(Number(v))}
                            />
                        </SectionShell>
                    )}

                    {/* Min Odds (AFL only) — uses a numeric input rather than a slider since users
                        typically know the exact decimal odds floor they want. */}
                    {strategy === 'market_and_implied_ev' && (
                        <SectionShell
                            label="Minimum Odds"
                            hint="Only show bets with best odds at or above this value."
                            enabled={!!localFilters.minOdds?.enabled}
                            onToggle={handleMinOddsToggle}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                                    Min Odds
                                </span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={localFilters.minOdds?.value ?? ''}
                                    onChange={(e) => handleMinOddsChange(e.target.value)}
                                    onBlur={handleMinOddsBlur}
                                    className="w-24 px-2 py-1.5 bg-slate-950/60 border border-slate-800 rounded text-xs font-mono text-white text-right tabular-nums focus:outline-none focus:border-emerald-500"
                                />
                            </div>
                        </SectionShell>
                    )}

                    {/* Match Start Time window (EV / MV) */}
                    {strategy !== 'arbs' && (
                        <SectionShell
                            label="Match Start Time"
                            hint="Only show games starting within this time window."
                            enabled={!!localFilters.startWithin?.enabled}
                            onToggle={handleStartWithinToggle}
                        >
                            <ThresholdSlider
                                valueLabel="Starts Within"
                                value={localFilters.startWithin?.value ?? 3}
                                min={0.5}
                                max={5}
                                step={0.5}
                                onChange={handleStartWithinChange}
                                formatValue={(v) => {
                                    const n = Number(v);
                                    if (n < 1) return `${Math.round(n * 60)}m`;
                                    return `${n}${n === 1 ? 'h' : 'h'}`;
                                }}
                                formatBound={(v) => (v < 1 ? `${Math.round(v * 60)}m` : `${v}h`)}
                            />
                        </SectionShell>
                    )}

                    {/* Exclude Bookmakers (Arbs) */}
                    {strategy === 'arbs' && (
                        <SectionShell
                            label="Exclude Bookmakers"
                            hint="Hide arbs involving any of the selected bookmakers."
                            enabled={!!localFilters.excludeBookmakers?.enabled}
                            onToggle={handleArbExcludeToggle}
                        >
                            {bookmakers && bookmakers.length > 0 && (
                                <>
                                    <div className="flex flex-wrap gap-1.5">
                                        {bookmakers.map(bk => (
                                            <BookmakerChip
                                                key={bk}
                                                bookmaker={bk}
                                                label={bookmakerNames?.[bk] || bk}
                                                logo={bookmakerLogos?.[bk]}
                                                selected={localFilters.excludeBookmakers.value.includes(bk)}
                                                onClick={handleArbExcludeBookmakerClick}
                                            />
                                        ))}
                                    </div>
                                    {localFilters.excludeBookmakers.value.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setLocalFilters(prev => ({
                                                    ...prev,
                                                    excludeBookmakers: { ...prev.excludeBookmakers, value: [] }
                                                }))
                                            }
                                            className="mt-3 text-[10px] font-mono text-slate-500 hover:text-slate-300 uppercase tracking-widest"
                                        >
                                            Clear ({localFilters.excludeBookmakers.value.length})
                                        </button>
                                    )}
                                </>
                            )}
                        </SectionShell>
                    )}

                    {/* Exclude Best Bookmakers (EV / MV) */}
                    {strategy !== 'arbs' && (
                        <SectionShell
                            label="Exclude Best Bookmakers"
                            hint="Hide bets where the best odds come from a selected bookmaker."
                            enabled={!!localFilters.excludeBestBookmakers?.enabled}
                            onToggle={handleExcludeBestToggle}
                        >
                            {bookmakers && bookmakers.length > 0 && (
                                <>
                                    <div className="flex flex-wrap gap-1.5">
                                        {bookmakers.map(bk => (
                                            <BookmakerChip
                                                key={bk}
                                                bookmaker={bk}
                                                label={bookmakerNames?.[bk] || bk}
                                                logo={bookmakerLogos?.[bk]}
                                                selected={localFilters.excludeBestBookmakers.value.includes(bk)}
                                                onClick={handleExcludeBookmakerClick}
                                            />
                                        ))}
                                    </div>
                                    {localFilters.excludeBestBookmakers.value.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setLocalFilters(prev => ({
                                                    ...prev,
                                                    excludeBestBookmakers: { ...prev.excludeBestBookmakers, value: [] }
                                                }))
                                            }
                                            className="mt-3 text-[10px] font-mono text-slate-500 hover:text-slate-300 uppercase tracking-widest"
                                        >
                                            Clear ({localFilters.excludeBestBookmakers.value.length})
                                        </button>
                                    )}
                                </>
                            )}
                        </SectionShell>
                    )}
                </div>

                {/* Footer */}
                <div className="relative flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-800/80 bg-slate-950/40">
                    <button
                        type="button"
                        onClick={handleReset}
                        className="text-[11px] font-mono text-slate-500 hover:text-slate-200 uppercase tracking-widest transition-colors py-2 md:py-0"
                    >
                        Reset
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 md:px-3.5 md:py-1.5 text-xs font-medium text-slate-300 rounded-md border border-slate-700/80 hover:bg-slate-800/60 hover:border-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleApply}
                            className="px-4 py-2 md:px-4 md:py-1.5 text-xs font-semibold text-emerald-950 bg-emerald-400 rounded-md hover:bg-emerald-300 transition-colors shadow-[0_0_0_1px_rgba(16,185,129,0.4)]"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default FiltersModal;
